import type { Segno, Song, SourceType } from '../types'
import { youtubeVideoId } from './youtube'

const FORMAT = 'mioSegno-segno-file'
const FORMAT_VERSION = 1
export const SEGNO_FILE_EXTENSION = '.segno'
export const SEGNO_FILE_MIME = 'application/vnd.miosegno+json'

export interface SegnoFileDocument {
  format: typeof FORMAT
  version: typeof FORMAT_VERSION
  exportedAt?: string
  title: string
  sourceType: SourceType
  youtubeUrl: string | null
  segnos: Array<{
    order: number
    title: string
    startTime: number
    endTime: number
    lyrics: string
  }>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function boundedText(value: unknown, label: string, max: number, allowEmpty = false): string {
  if (typeof value !== 'string') throw new Error(`${label} 형식이 올바르지 않습니다.`)
  const normalized = value.trim()
  if (!allowEmpty && !normalized) throw new Error(`${label}이 비어 있습니다.`)
  if (normalized.length > max) throw new Error(`${label}이 너무 깁니다.`)
  return allowEmpty ? value : normalized
}

function finiteNumber(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error(`${label} 형식이 올바르지 않습니다.`)
  return value
}

export function buildSegnoFileDocument(song: Song): SegnoFileDocument {
  return {
    format: FORMAT,
    version: FORMAT_VERSION,
    exportedAt: new Date().toISOString(),
    title: song.title,
    sourceType: song.sourceType,
    // FILE 작업물에서는 서버 mediaFileId/음원 자체를 절대 내보내지 않습니다.
    youtubeUrl: song.sourceType === 'YOUTUBE' ? (song.youtubeUrl ?? null) : null,
    segnos: [...song.segnos]
      .sort((left, right) => left.order - right.order)
      .map((segno, index) => ({
        order: index + 1,
        title: segno.title,
        startTime: segno.startTime,
        endTime: segno.endTime,
        lyrics: segno.lyrics ?? '',
      })),
  }
}

export function serializeSegnoFile(song: Song): string {
  return `${JSON.stringify(buildSegnoFileDocument(song), null, 2)}\n`
}

export function parseSegnoFile(text: string): SegnoFileDocument {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new Error('세뇨 파일이 올바른 JSON이 아닙니다.')
  }
  if (!isRecord(parsed) || parsed.format !== FORMAT || parsed.version !== FORMAT_VERSION) {
    throw new Error('지원하는 mioSegno 세뇨 파일이 아닙니다.')
  }

  const title = boundedText(parsed.title, '곡 제목', 255)
  const sourceType = parsed.sourceType
  if (sourceType !== 'FILE' && sourceType !== 'YOUTUBE') {
    throw new Error('세뇨 파일의 원본 종류가 올바르지 않습니다.')
  }

  let youtubeUrl: string | null = null
  if (sourceType === 'YOUTUBE') {
    youtubeUrl = boundedText(parsed.youtubeUrl, '유튜브 주소', 512)
    if (!youtubeVideoId(youtubeUrl)) throw new Error('세뇨 파일의 유튜브 주소가 올바르지 않습니다.')
  }

  if (!Array.isArray(parsed.segnos) || parsed.segnos.length < 1 || parsed.segnos.length > 100) {
    throw new Error('세뇨 파일에는 1~100개의 세뇨가 있어야 합니다.')
  }

  const orders = new Set<number>()
  const segnos = parsed.segnos.map((raw, index) => {
    if (!isRecord(raw)) throw new Error(`${index + 1}번 세뇨 형식이 올바르지 않습니다.`)
    const order = finiteNumber(raw.order, `${index + 1}번 세뇨 순서`)
    if (!Number.isInteger(order) || order < 1 || orders.has(order)) throw new Error('세뇨 순서가 올바르지 않거나 중복되었습니다.')
    orders.add(order)

    const segnoTitle = boundedText(raw.title, `${index + 1}번 세뇨 이름`, 100)
    const startTime = finiteNumber(raw.startTime, `${index + 1}번 A 시작`)
    const endTime = finiteNumber(raw.endTime, `${index + 1}번 B 끝`)
    if (startTime < 0 || endTime <= startTime) throw new Error(`${index + 1}번 세뇨의 A/B 시간이 올바르지 않습니다.`)
    const lyrics = raw.lyrics == null ? '' : boundedText(raw.lyrics, `${index + 1}번 가사`, 20000, true)
    return { order, title: segnoTitle, startTime, endTime, lyrics }
  })

  segnos.sort((left, right) => left.order - right.order)
  return {
    format: FORMAT,
    version: FORMAT_VERSION,
    exportedAt: typeof parsed.exportedAt === 'string' ? parsed.exportedAt : undefined,
    title,
    sourceType,
    youtubeUrl,
    segnos: segnos.map((segno, index) => ({ ...segno, order: index + 1 })),
  }
}

function safeFilename(title: string): string {
  const normalized = title
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
  return normalized || 'mioSegno'
}

export function downloadSegnoFile(song: Song): void {
  const blob = new Blob([serializeSegnoFile(song)], { type: `${SEGNO_FILE_MIME};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${safeFilename(song.title)}${SEGNO_FILE_EXTENSION}`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function segnosFromFile(document: SegnoFileDocument): Segno[] {
  return document.segnos.map((segno) => ({ ...segno }))
}
